import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Alert,
  Linking,
  Platform,
  Share,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { storageService } from '../services/storageService';
import { callCancelFoodOrder } from '../services/cloudFunctions';
import { doc, onSnapshot } from 'firebase/firestore';
// SDK NATIVO de Firestore. En Android el SDK web escribe SIN SESIÓN (el login
// vive en @react-native-firebase vía authBridge) y la regla de update de
// /food_orders exige que el uid sea el customer: la escritura se denegaba.
// El daño concreto: la foto SÍ subía a Storage (gastando los datos del
// cliente) y justo después fallaba el guardado de la URL, así que el
// comprobante quedaba huérfano y el cliente veía un error tras esperar.
// Mismo patrón que services/firestoreService.ts.
import nativeFirestore from '@react-native-firebase/firestore';
import { db } from '../services/firebaseConfig';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';
import { preguntarAntesDeAlgoGrave, avisar } from '../utils/preguntar';

/**
 * RECIBO UNIFICADO DE PEDIDO
 * ----------------------------------------------------------------------------
 * Funciona para los 3 métodos de pago:
 *
 *   CASH:     muestra "Pagado en efectivo · entregado a [repartidor]"
 *             botón compartir como prueba
 *
 *   TRANSFER: muestra la foto del comprobante subida + cuenta bancaria
 *             del restaurante. Si está en validación, banner amarillo.
 *
 *   CARD:     muestra "Pagado con tarjeta ****1234 · 0% comisión Go Safely"
 *             botón "Ver recibo oficial Stripe" (link al receipt_url)
 *
 * Además:
 *   - Botón CANCELAR si el pedido aún se puede cancelar
 *     (PENDING/PREPARING para customer; con refund auto si era CARD)
 *   - Compartir por WhatsApp/Email/SMS (link nativo Share)
 *   - PIN visible para entregar al repartidor
 */
/**
 * Estado del PIN de entrega. Son CUATRO cosas distintas y antes las cuatro se
 * pintaban igual ("----"):
 *   CARGANDO — todavía no contestó Firestore.
 *   OK       — tenemos el PIN.
 *   AUSENTE  — el pedido no tiene PIN (pedidos viejos, anteriores a la
 *              subcolección privada). No hay nada que dictar al repartidor.
 *   ERROR    — la lectura falló (conexión, o el pedido es de otra cuenta).
 *              Aquí "----" es una mentira peligrosa: el cliente cree que su
 *              pedido no tiene PIN y le abre la puerta a cualquiera.
 */
type EstadoPin = 'CARGANDO' | 'OK' | 'AUSENTE' | 'ERROR';

/**
 * Ajusta el comprobante antes de subirlo: ancho máximo 1400 px y JPEG al 70%.
 *
 * NO se recorta a un aspecto fijo a propósito (a diferencia de `resizeToAspect`
 * de storageService, que es para portadas): un comprobante recortado pierde
 * justo el número de referencia o el monto, que es lo único que el restaurante
 * necesita leer. 1400 px de ancho deja el texto perfectamente legible y baja la
 * foto muy por debajo del tope de 5 MB de la regla de Storage.
 *
 * Si el manipulador falla (URI exótica, web raro), se sube la original: es
 * mejor intentarlo y que el tope decida, que no subir nada.
 */
async function reducirComprobante(uri: string): Promise<string> {
  try {
    const salida = await ImageManipulator.manipulateAsync(
      uri,
      [{ resize: { width: 1400 } }],
      { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
    );
    return salida.uri;
  } catch (e: any) {
    console.warn('[receipt] no se pudo redimensionar, subo la original:', e?.message ?? e);
    return uri;
  }
}

export default function OrderReceiptScreen() {
  const router = useRouter();
  const { orderId } = useLocalSearchParams<{ orderId: string }>();

  const [order, setOrder] = useState<any | null>(null);
  const [orderPin, setOrderPin] = useState<string | null>(null);
  const [estadoPin, setEstadoPin] = useState<EstadoPin>('CARGANDO');
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  // H15 — el comprobante vive en `private/comprobante`, fuera del documento que
  // ve el feed de repartidores. Los pedidos viejos aún lo traen en el principal.
  const [comprobantePrivado, setComprobantePrivado] = useState<string | null>(null);
  const [direccionPrivada, setDireccionPrivada] = useState<string | null>(null);
  const userUid = useAppStore((s) => s.userFirebaseUid);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setEstadoPin('ERROR');
      return;
    }
    const unsub = firestoreService.listenToFoodOrderById(orderId, (o) => {
      setOrder(o);
      setLoading(false);
    });

    // PIN desde la subcolección privada /food_orders/{id}/private/security
    // (regla: solo el customer del pedido puede leerla).
    //
    // La suscripción va AQUÍ y no en `firestoreService.listenToOrderPin`
    // porque esa función colapsa "no hay PIN" y "no se pudo leer" en el mismo
    // `null`, y con un solo null es imposible distinguirlos en pantalla.
    const recibir = (data: any | null) => {
      const pin = String(data?.pin ?? '').trim();
      if (pin) { setOrderPin(pin); setEstadoPin('OK'); }
      else { setOrderPin(null); setEstadoPin('AUSENTE'); }
    };
    const fallar = (e: any) => {
      console.warn('[receipt] no se pudo leer el PIN:', e?.message ?? e);
      setOrderPin(null);
      setEstadoPin('ERROR');
    };

    let unsubPin: () => void;
    if (Platform.OS !== 'web') {
      unsubPin = nativeFirestore()
        .collection('food_orders').doc(orderId)
        .collection('private').doc('security')
        .onSnapshot(
          // En @react-native-firebase v24 `exists` es MÉTODO, no propiedad.
          (snap: any) => recibir(snap?.exists?.() ? snap.data() : null),
          (e: any) => fallar(e)
        ) as () => void;
    } else {
      unsubPin = onSnapshot(
        doc(db, 'food_orders', orderId, 'private', 'security'),
        (snap) => recibir(snap.exists() ? snap.data() : null),
        (e) => fallar(e)
      ) as () => void;
    }

    const unsubComprobante = firestoreService.escucharPrivadoDelPedido(
      orderId,
      'comprobante',
      (data) => setComprobantePrivado(String(data?.receiptImage ?? '').trim() || null)
    );

    // V21 — la direccion de entrega tambien es dato privado del pedido.
    const unsubContacto = firestoreService.escucharPrivadoDelPedido(
      orderId,
      'contacto',
      (data) => setDireccionPrivada(String(data?.deliveryAddress ?? '').trim() || null)
    );

    return () => {
      unsub();
      try { unsubPin?.(); } catch { /* ya estaba cerrada */ }
      try { unsubComprobante(); } catch { /* ya estaba cerrada */ }
      try { unsubContacto(); } catch { /* ya estaba cerrada */ }
    };
  }, [orderId]);

  const urlComprobante: string | null = comprobantePrivado ?? (order?.receiptImage || null);
  const direccionDelPedido: string | null =
    direccionPrivada ?? (String(order?.deliveryAddress ?? '').trim() || null);

  const handleShare = async () => {
    if (!order) return;
    try {
      await Share.share({
        message: `🧾 Recibo Go Safely\n\n` +
          `Pedido: ${order.id}\n` +
          `Restaurante: ${order.restaurantName ?? '—'}\n` +
          `Items: ${order.items ?? '—'}\n` +
          `Total: ${pesos(order.total ?? 0)}\n` +
          `Método: ${methodLabel(order.paymentMethod)}\n` +
          `Estado: ${statusLabel(order.status)}\n` +
          // Compartir un "PIN: ----" es compartir un dato falso. Si no lo
          // tenemos, la línea del PIN no va.
          (orderPin ? `PIN: ${orderPin}\n` : '') + `\n` +
          `Go Safely · 0% comisión a los negocios`,
        title: 'Recibo de tu pedido',
      });
    } catch (e: any) {
      console.warn('[receipt] share falló:', e?.message);
    }
  };

  /**
   * Subir comprobante (foto/screenshot del pago) — útil para:
   *   - Cliente que se le olvidó subir comprobante de TRANSFER al hacer el pedido
   *   - Cliente que quiere subir prueba post-pago para sus records
   *   - Cliente CASH que quiere subir foto del recibo del repartidor
   *
   * Sube la imagen a Storage privado y actualiza el doc del pedido con
   * la URL. Solo el customer del pedido puede hacerlo (Firestore rules).
   */
  const handleUploadReceipt = async () => {
    if (!order) return;
    // Sin sesión el botón se quedaba MUDO: tocarlo no hacía absolutamente nada.
    // Un botón que no responde es literalmente uno de los motivos por los que
    // Google ya rechazó la app; decir por qué cuesta lo mismo.
    if (!userUid) {
      Alert.alert(
        'Inicia sesión',
        'Para subir el comprobante de tu pedido necesitamos saber que eres tú. Inicia sesión con la cuenta que hizo el pedido.'
      );
      return;
    }
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        // `MediaTypeOptions` está obsoleto en el SDK actual; el array es la
        // forma vigente (la misma que usa wallet.tsx).
        mediaTypes: ['images'],
        quality: 0.8,
        allowsEditing: true,
      });
      if (result.canceled || !result.assets?.[0]?.uri) return;

      setUploadingReceipt(true);
      // REDIMENSIONAR ANTES DE SUBIR. La regla de Storage para /receipts/{uid}
      // tope en 5 MB, y `quality: 0.8` sin redimensionar deja la foto a la
      // resolución completa del sensor: una cámara de 12 MP pasa de 5 MB con
      // facilidad y la subida se rechaza SIEMPRE. El cliente ya gastó sus datos
      // y solo ve un error.
      const uriAjustada = await reducirComprobante(result.assets[0].uri);
      // uploadOrderReceipt acepta URI directo y usa putFile nativo en móvil.
      const url = await storageService.uploadOrderReceipt(order.id, userUid, uriAjustada);

      // Guardar el enlace en la parte PRIVADA del pedido (H15), con el SDK que
      // sí lleva la sesión en cada plataforma. La hora la pone el servidor: el
      // reloj del teléfono puede estar corrido.
      await firestoreService.guardarComprobanteDelPedido(order.id, url);
      setComprobantePrivado(url);
      Alert.alert('Comprobante subido', 'Tu comprobante fue guardado correctamente.');
    } catch (e: any) {
      console.warn('[receipt upload]', e?.code, e?.message);
      // Un "permission-denied" aquí solo puede significar que quien mira el
      // recibo no es el dueño del pedido. Decírselo con el código crudo de
      // Firestore no le sirve de nada.
      const permiso = String(e?.code ?? '').includes('permission-denied');
      Alert.alert(
        'No pudimos guardar tu comprobante',
        permiso
          ? 'Solo quien hizo el pedido puede subirle el comprobante. Inicia sesión con esa cuenta e intenta de nuevo.'
          : 'La foto no llegó a guardarse. Revisa tu conexión e intenta de nuevo; no se te cobró nada por esto.'
      );
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    // ── EL REEMBOLSO SE PROMETÍA DONDE NO LO HAY ────────────────────────
    //
    // El aviso decía "Tu reembolso se procesa automáticamente (5-10 días)" con
    // una sola condición: CARD + PAID. Pero `cancelFoodOrder`
    // (functions/src/cancelOrder.ts) solo crea el refund de Stripe si además
    // existe `stripePaymentIntentId`; sin él la orden se cancela y no se
    // devuelve nada, y el cliente se quedaba esperando diez días un dinero que
    // nadie pidió. Ahora la promesa cuelga de la MISMA condición que el código
    // que la cumple, y habla de Stripe —que es quien tarda— en vez de
    // prometerlo Go Safely.
    //
    // El otro camino es el real hoy: efectivo o transferencia. Ahí los
    // Términos (punto 8) son explícitos — Go Safely no recibió ese dinero y no
    // puede devolverlo; lo devuelve el restaurante y nosotros mediamos si
    // escribes dentro de 24 horas. Callarlo dejaba al cliente creyendo que
    // cancelar era lo mismo que recuperar su dinero.
    const yaPagoConTarjeta = order.paymentMethod === 'CARD' && order.paymentStatus === 'PAID';
    const habraReembolso = yaPagoConTarjeta && !!order.stripePaymentIntentId;
    // V38 — TRANSFER y CASH nacen con paymentStatus 'NOT_APPLICABLE' y nada lo
    // pasa a PAID (orders.ts), así que este aviso no salía nunca. Una
    // transferencia se paga ANTES de pedir (el comprobante es obligatorio):
    // basta el método. El efectivo se paga en la puerta; solo cuenta si algún
    // pedido viejo quedó marcado PAID.
    const pagoDirectoAlRestaurante =
      order.paymentMethod === 'TRANSFER' ||
      (order.paymentMethod === 'CASH' && order.paymentStatus === 'PAID');
    // ⚠️ ESTO ERA `Alert.alert` Y EN LA WEB SE PERDÍA: el `onPress` de «Sí,
    // cancelar» —que es el que pide el reembolso a Stripe— no se ejecuta nunca
    // en el navegador, porque ahí `Alert` es una función vacía. El cliente
    // pulsaba cancelar, no pasaba nada, y su dinero seguía cobrado.
    const confirmado = await preguntarAntesDeAlgoGrave(
      'Cancelar pedido',
      habraReembolso
        ? `¿Seguro que quieres cancelar? Pediremos a Stripe el reembolso de ${pesos(order.total)}; el banco suele tardar entre 5 y 10 días hábiles en reflejarlo.`
        : yaPagoConTarjeta
        ? `¿Seguro que quieres cancelar? Este pedido no tiene un cobro que podamos revertir solos: escríbenos por soporte para resolver la devolución de ${pesos(order.total)}.`
        : pagoDirectoAlRestaurante
        ? `¿Seguro que quieres cancelar? Le pagaste directamente al negocio, así que Go Safely no puede devolverte ese dinero: la devolución de ${pesos(order.total)} la hace el negocio. Pídesela a ellos y, si no responden, escríbenos por soporte dentro de las 24 horas y mediamos.`
        : `¿Seguro que quieres cancelar este pedido?`,
      'No',
      'Sí, cancelar'
    );
    if (!confirmado) return;
    setCancelling(true);
    try {
      const r = await callCancelFoodOrder({ orderId: order.id });
      avisar('Pedido cancelado', r.data?.message ?? 'Tu pedido fue cancelado.');
    } catch (e: any) {
      avisar('Error', mensajeDeError(e, 'No pudimos cancelar el pedido. Intenta de nuevo o escríbenos por soporte.'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </SafeAreaView>
    );
  }

  if (!order) {
    // `listenToFoodOrderById` colapsa "no existe" y "no se pudo leer" en el
    // mismo `null`, así que aquí no podemos afirmar que el pedido no existe:
    // sería decirle a alguien que acaba de pagar que su pedido desapareció.
    // Y sin este botón la pantalla era un callejón sin salida (no se pinta el
    // header con la flecha en esta rama).
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 24 }]}>
        <Ionicons name="alert-circle-outline" size={56} color={Colors.border} />
        <Text style={{ marginTop: 12, color: Colors.text, fontWeight: '700', fontSize: 16, textAlign: 'center' }}>
          No pudimos mostrar este recibo
        </Text>
        <Text style={{ marginTop: 8, color: Colors.textSecondary, textAlign: 'center', lineHeight: 20 }}>
          Puede ser tu conexión, o que este pedido pertenezca a otra cuenta. Tu pedido
          sigue en tu historial: vuelve a Actividad e intenta de nuevo.
        </Text>
        <TouchableOpacity
          onPress={() => volver(router)}
          style={{ marginTop: 22, backgroundColor: Colors.primary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 12 }}
        >
          <Text style={{ color: '#FFF', fontWeight: '700' }}>Volver</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const canCancel = ['PENDING', 'PREPARING'].includes(order.status);

  // ─── DESGLOSE CON LOS CAMPOS QUE EL SERVIDOR GUARDA DE VERDAD ───────────
  //
  // `deliveryFee` NO EXISTE en /food_orders. `createFoodOrder`
  // (functions/src/orders.ts) escribe: `subtotal` (solo la comida, calculado
  // contra el menú real), `grossTotal` (bruto antes del cupón), `tip`,
  // `couponDiscount` y `total` (neto que se cobra). El delivery no se guarda
  // como campo propio: va dentro del bruto.
  //
  // Consecuencia de leer un campo inexistente: la fila "Delivery" NUNCA se
  // pintaba (su condición era `deliveryFee > 0`, y `undefined ?? 0` = 0) y ese
  // mismo cero hacía que el subtotal calculado se tragara el costo del envío.
  // El cliente veía la comida más cara de lo que era y ningún cargo de envío
  // por ninguna parte — el desglose no cuadraba con lo que le cobraron.
  //
  // Ahora: comida = `subtotal` del servidor, y el envío es lo que queda del
  // bruto una vez quitados la comida y la propina (que es exactamente como el
  // servidor deriva los extras: `extras = total_bruto − subtotal`).
  const num = (v: any): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const totalNeto = num(order.total);
  const propina = num(order.tip);
  const cupon = num(order.couponDiscount);
  // La promoción del negocio: el 50% del primer pedido. Lo descuenta
  // `createFoodOrder` y lo guarda en `promoDiscount`; los pedidos de antes de
  // que las promociones funcionaran no lo traen, y `num` los deja en 0.
  const promo = num(order.promoDiscount);
  // El bruto lo guarda el servidor; en pedidos anteriores a ese campo se
  // reconstruye sumando el cupón al neto (el neto ya lo trae restado).
  const bruto = Number.isFinite(Number(order.grossTotal)) ? Number(order.grossTotal) : totalNeto + cupon + promo;
  // Subtotal de COMIDA. Si el pedido es viejo y no lo trae, no lo inventamos:
  // se enseña una sola línea honesta con comida y envío juntos.
  const subtotalComida = Number.isFinite(Number(order.subtotal)) ? Number(order.subtotal) : null;
  // `deliveryFee` ya lo guarda el servidor (es lo que explica el comentario de
  // arriba: antes NO existía y había que deducirlo). Se prefiere el campo, y
  // la resta queda de respaldo para los pedidos que nacieron sin él.
  const delivery = Number.isFinite(Number(order.deliveryFee))
    ? Math.max(0, Number(order.deliveryFee))
    : subtotalComida === null
    ? null
    : Math.max(0, Math.round(bruto - subtotalComida - propina));
  const envioGratisPorPromo = order.envioGratisPorPromo === true;

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Recibo</Text>
        <TouchableOpacity onPress={handleShare} style={styles.shareBtn}>
          <Ionicons name="share-social-outline" size={22} color={Colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={{ padding: SIZES.padding, paddingBottom: 60 }}>
        {/* HERO con monto */}
        <View style={[styles.hero, statusBg(order.status)]}>
          <Ionicons name={statusIcon(order.status)} size={40} color="#FFF" />
          <Text style={styles.heroAmount}>{pesos(order.total ?? 0)}</Text>
          <Text style={styles.heroStatus}>{statusLabel(order.status)}</Text>
        </View>

        {/* PIN si el pedido sigue activo.
            Cada estado dice lo suyo: "----" para todo hacía que un fallo de
            lectura pareciera un pedido sin PIN, y el cliente entregaba su
            pedido sin verificar nada. */}
        {!['DELIVERED', 'CANCELLED'].includes(order.status) && (
          <View style={[styles.pinBox, estadoPin === 'ERROR' && styles.pinBoxError]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.pinLabel, estadoPin === 'ERROR' && { color: '#B71C1C' }]}>
                PIN para el repartidor
              </Text>
              {estadoPin === 'OK' ? (
                <Text style={styles.pinValue}>{orderPin}</Text>
              ) : estadoPin === 'CARGANDO' ? (
                <Text style={styles.pinAviso}>Buscando tu PIN…</Text>
              ) : estadoPin === 'ERROR' ? (
                <Text style={[styles.pinAviso, { color: '#B71C1C' }]}>
                  No pudimos leer tu PIN. NO es que no tengas: revisa tu conexión y vuelve
                  a abrir el recibo antes de recibir el pedido.
                </Text>
              ) : (
                <Text style={styles.pinAviso}>
                  {/* ⚠️ ESTO LE ENSEÑABA AL CLIENTE A ACEPTAR COMIDA SIN CÓDIGO.
                      Decía «Este pedido no tiene PIN de entrega. El repartidor te
                      lo entrega sin código.» — y es falso por los dos lados: el
                      servidor NO deja cerrar una entrega sin PIN correcto
                      (`deliverFoodOrder`, functions/src/foodDispatch.ts), y a la
                      vez le quitaba al cliente la única comprobación que tiene de
                      que quien está en su puerta es su repartidor.
                      Si no hay código es que algo falló, no que no haga falta. */}
                  No pudimos cargar tu código de entrega. NO se lo des a nadie ni
                  aceptes el pedido sin él: escríbenos por soporte y lo
                  resolvemos.
                </Text>
              )}
            </View>
            <Ionicons
              name={estadoPin === 'ERROR' ? 'alert-circle' : 'key'}
              size={28}
              color={estadoPin === 'ERROR' ? '#B71C1C' : '#FF9500'}
            />
          </View>
        )}

        {/* Restaurante */}
        <View style={styles.card}>
          <Text style={styles.cardLabel}>Negocio</Text>
          <Text style={styles.cardValue}>{order.restaurantName ?? '—'}</Text>
        </View>

        {/* Items */}
        <View style={styles.card}>
          <Text style={styles.cardLabel}>Productos</Text>
          <Text style={[styles.cardValue, { fontSize: 14 }]}>{order.items ?? '—'}</Text>
        </View>

        {/* Dirección entrega */}
        <View style={styles.card}>
          <Text style={styles.cardLabel}>Dirección de entrega</Text>
          {/* V21 — la dirección vive en `private/contacto` (el pedido lo lee el
              feed de repartidores). El cliente sí tiene permiso de leerlo. */}
          <Text style={[styles.cardValue, { fontSize: 14 }]}>
            {direccionDelPedido ?? order.zonaEntrega ?? '—'}
          </Text>
        </View>

        {/* Desglose */}
        <View style={styles.summary}>
          {subtotalComida !== null ? (
            <>
              <View style={styles.summaryRow}>
                <Text style={styles.sumLbl}>Comida</Text>
                <Text style={styles.sumVal}>{pesos(subtotalComida)}</Text>
              </View>
              {delivery !== null && delivery > 0 && (
                <View style={styles.summaryRow}>
                  <Text style={styles.sumLbl}>Delivery</Text>
                  <Text style={styles.sumVal}>{pesos(delivery)}</Text>
                </View>
              )}
              {/* Que quede por escrito lo que el negocio le regaló: si no se
                  dice, un envío en RD$0 parece que la app no cobra envío. */}
              {envioGratisPorPromo && (
                <View style={styles.summaryRow}>
                  <Text style={[styles.sumLbl, { color: '#00A86B' }]}>Delivery</Text>
                  <Text style={[styles.sumVal, { color: '#00A86B', fontWeight: '700' }]}>
                    Gratis (lo asumió el negocio)
                  </Text>
                </View>
              )}
            </>
          ) : (
            /* Pedido anterior al campo `subtotal`: no sabemos cuánto de ese
               monto era comida y cuánto envío, así que se dice tal cual en vez
               de repartirlo a ojo. */
            <View style={styles.summaryRow}>
              <Text style={styles.sumLbl}>Comida y delivery</Text>
              <Text style={styles.sumVal}>{pesos(Math.max(0, Math.round(bruto - propina)))}</Text>
            </View>
          )}
          {propina > 0 && (
            <View style={styles.summaryRow}>
              <Text style={styles.sumLbl}>Propina al repartidor</Text>
              <Text style={styles.sumVal}>{pesos(propina)}</Text>
            </View>
          )}
          {promo > 0 && (
            <View style={styles.summaryRow}>
              <Text style={[styles.sumLbl, { color: '#00A86B' }]}>
                {typeof order.promoTitulo === 'string' && order.promoTitulo.trim()
                  ? order.promoTitulo
                  : 'Oferta del negocio'}
              </Text>
              <Text style={[styles.sumVal, { color: '#00A86B', fontWeight: '700' }]}>− {pesos(promo)}</Text>
            </View>
          )}
          {cupon > 0 && (
            <View style={styles.summaryRow}>
              <Text style={[styles.sumLbl, { color: '#00A86B' }]}>Cupón {order.couponCode}</Text>
              <Text style={[styles.sumVal, { color: '#00A86B', fontWeight: '700' }]}>− {pesos(cupon)}</Text>
            </View>
          )}
          <View style={[styles.summaryRow, styles.totalRow]}>
            <Text style={styles.totalLbl}>Total</Text>
            <Text style={styles.totalVal}>{pesos(totalNeto)}</Text>
          </View>
        </View>

        {/* MÉTODO DE PAGO + estado específico */}
        <View style={styles.paymentBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <Ionicons name={methodIcon(order.paymentMethod)} size={20} color={Colors.primary} />
            <Text style={styles.paymentMethod}>{methodLabel(order.paymentMethod)}</Text>
          </View>

          {/* CARD */}
          {order.paymentMethod === 'CARD' && (
            <>
              <PaymentStatusRow status={order.paymentStatus} />
              {order.stripeReceiptUrl && (
                <TouchableOpacity
                  style={styles.receiptLink}
                  onPress={() => Linking.openURL(order.stripeReceiptUrl)}
                >
                  <Ionicons name="document-text" size={16} color={Colors.primary} />
                  <Text style={styles.receiptLinkText}>Ver recibo oficial de Stripe</Text>
                  <Ionicons name="open-outline" size={14} color={Colors.primary} />
                </TouchableOpacity>
              )}
            </>
          )}

          {/* TRANSFER */}
          {order.paymentMethod === 'TRANSFER' && (
            <>
              {urlComprobante ? (
                <>
                  <Text style={styles.receiptCaption}>Tu comprobante subido:</Text>
                  <TouchableOpacity
                    onPress={() => Linking.openURL(urlComprobante)}
                    activeOpacity={0.85}
                  >
                    <Image
                      source={{ uri: urlComprobante }}
                      style={styles.receiptImage}
                      resizeMode="contain"
                    />
                    <Text style={styles.receiptHint}>Toca para ampliar</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <Text style={styles.muted}>Sin comprobante subido.</Text>
              )}
              {order.status === 'PENDING' && (
                <View style={styles.pendingValidation}>
                  <Ionicons name="hourglass-outline" size={14} color="#6B5500" />
                  <Text style={styles.pendingValidationText}>
                    Esperando que el negocio valide tu transferencia
                  </Text>
                </View>
              )}
            </>
          )}

          {/* CASH */}
          {order.paymentMethod === 'CASH' && (
            <View style={styles.cashInfo}>
              <Ionicons name="cash-outline" size={18} color="#1B5E20" />
              <Text style={styles.cashInfoText}>
                {order.status === 'DELIVERED'
                  ? `Pagaste ${pesos(order.total)} en efectivo al recibir tu pedido.`
                  : `Pagarás ${pesos(order.total)} en efectivo al recibir tu pedido.`}
              </Text>
            </View>
          )}
        </View>

        {/* ACCIONES */}
        <View style={styles.actions}>
          {/* Subir / actualizar comprobante de pago */}
          <TouchableOpacity
            style={[styles.uploadBtn, uploadingReceipt && { opacity: 0.6 }]}
            onPress={handleUploadReceipt}
            disabled={uploadingReceipt}
          >
            {uploadingReceipt ? (
              <ActivityIndicator color="#FFF" />
            ) : (
              <>
                <Ionicons name={urlComprobante ? 'refresh-circle' : 'cloud-upload'} size={18} color="#FFF" />
                <Text style={styles.uploadBtnText}>
                  {urlComprobante ? 'Actualizar comprobante' : 'Subir comprobante de pago'}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {canCancel && (
            <TouchableOpacity
              style={[styles.cancelBtn, cancelling && { opacity: 0.6 }]}
              onPress={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <>
                  <Ionicons name="close-circle" size={18} color="#FFF" />
                  <Text style={styles.cancelBtnText}>Cancelar pedido</Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>

        <Text style={styles.footer}>
          © 2026 Go Safely · 0% comisión a los negocios · 100% al repartidor
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers visuales
// ────────────────────────────────────────────────────────────────────────

function PaymentStatusRow({ status }: { status?: string }) {
  if (!status || status === 'NOT_APPLICABLE') return null;
  const map: Record<string, { label: string; color: string; icon: keyof typeof Ionicons.glyphMap }> = {
    PENDING_CONFIRMATION: { label: 'Pago en proceso', color: '#FF9500', icon: 'time' },
    PAID:                 { label: 'Pago confirmado',  color: '#2E7D32', icon: 'checkmark-circle' },
    FAILED:               { label: 'Pago rechazado',   color: '#D32F2F', icon: 'close-circle' },
    REFUNDED:             { label: 'Reembolsado',      color: '#1565C0', icon: 'cash' },
  };
  const info = map[status] ?? { label: status, color: Colors.textSecondary, icon: 'help' as const };
  return (
    <View style={[styles.statusPill, { backgroundColor: info.color + '20' }]}>
      <Ionicons name={info.icon} size={14} color={info.color} />
      <Text style={[styles.statusPillText, { color: info.color }]}>{info.label}</Text>
    </View>
  );
}

function statusLabel(s: string): string {
  return {
    PENDING: 'Pendiente',
    PREPARING: 'En preparación',
    READY: 'Listo para recoger',
    ASSIGNED: 'Repartidor en camino',
    PICKED_UP: 'En camino a ti',
    DELIVERED: 'Entregado',
    CANCELLED: 'Cancelado',
  }[s] ?? s;
}

function statusIcon(s: string): keyof typeof Ionicons.glyphMap {
  return ({
    PENDING: 'time',
    PREPARING: 'restaurant',
    READY: 'fast-food',
    ASSIGNED: 'bicycle',
    PICKED_UP: 'navigate',
    DELIVERED: 'checkmark-circle',
    CANCELLED: 'close-circle',
  } as const)[s] ?? 'document';
}

function statusBg(s: string) {
  const map: Record<string, string> = {
    PENDING: '#FF9500',
    PREPARING: '#FF9500',
    READY: '#0066CC',
    ASSIGNED: '#0066CC',
    PICKED_UP: '#00A86B',
    DELIVERED: '#2E7D32',
    CANCELLED: '#D32F2F',
  };
  return { backgroundColor: map[s] ?? Colors.primary };
}

function methodLabel(m?: string): string {
  return { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia bancaria' }[m ?? ''] ?? 'Pago';
}

function methodIcon(m?: string): keyof typeof Ionicons.glyphMap {
  return ({ CASH: 'cash', CARD: 'card', TRANSFER: 'business' } as const)[m ?? ''] ?? 'card';
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F5F7FA' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#FFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5 },
  shareBtn: { padding: 5 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: Colors.text },

  hero: {
    padding: 24,
    borderRadius: 18,
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 4,
  },
  heroAmount: { color: '#FFF', fontSize: 40, fontWeight: '900', marginTop: 6, letterSpacing: -1 },
  heroStatus: { color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: '700', marginTop: 4, letterSpacing: 0.5 },

  pinBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    padding: 16,
    borderRadius: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#FFE082',
  },
  pinBoxError: { backgroundColor: '#FFEBEE', borderColor: '#FFCDD2' },
  pinLabel: { fontSize: 11, color: '#8B6E00', fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase' },
  pinValue: { fontSize: 28, color: '#5D4500', fontWeight: '900', marginTop: 2, letterSpacing: 4 },
  pinAviso: { fontSize: 12.5, color: '#6B5500', marginTop: 4, lineHeight: 18 },

  card: {
    backgroundColor: '#FFF',
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },
  cardLabel: { fontSize: 11, color: Colors.textSecondary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontSize: 15, color: Colors.text, fontWeight: '600', marginTop: 4 },

  summary: { backgroundColor: '#FFF', padding: 16, borderRadius: 14, marginBottom: 12, marginTop: 6 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  sumLbl: { fontSize: 14, color: Colors.textSecondary },
  sumVal: { fontSize: 14, color: Colors.text, fontWeight: '600' },
  totalRow: { borderTopWidth: 1, borderTopColor: '#EEE', marginTop: 6, paddingTop: 12 },
  totalLbl: { fontSize: 17, fontWeight: '800', color: Colors.text },
  totalVal: { fontSize: 17, fontWeight: '900', color: Colors.primary },

  paymentBox: { backgroundColor: '#FFF', padding: 16, borderRadius: 14, marginBottom: 12 },
  paymentMethod: { fontSize: 15, fontWeight: '700', color: Colors.text, marginLeft: 8 },

  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 10,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  statusPillText: { fontSize: 12, fontWeight: '700', marginLeft: 5 },

  receiptLink: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EAF4FF',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  receiptLinkText: { color: Colors.primary, fontWeight: '700', fontSize: 13, marginHorizontal: 8, flex: 1 },

  receiptCaption: { fontSize: 12, color: Colors.textSecondary, marginBottom: 6 },
  receiptImage: { width: '100%', height: 220, borderRadius: 10, backgroundColor: '#F0F0F0' },
  receiptHint: { fontSize: 11, color: Colors.textSecondary, textAlign: 'center', marginTop: 4, fontStyle: 'italic' },
  muted: { color: Colors.textSecondary, fontSize: 13, fontStyle: 'italic' },

  pendingValidation: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  pendingValidationText: { fontSize: 11, color: '#8B6E00', marginLeft: 6, flex: 1 },

  cashInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    padding: 10,
    borderRadius: 8,
  },
  cashInfoText: { fontSize: 13, color: '#1B5E20', marginLeft: 8, flex: 1 },

  actions: { marginTop: 6 },
  cancelBtn: {
    backgroundColor: '#D32F2F',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 12,
  },
  cancelBtnText: { color: '#FFF', fontWeight: '700', fontSize: 14, marginLeft: 8 },
  uploadBtn: {
    backgroundColor: '#0066CC',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
  },
  uploadBtnText: { color: '#FFF', fontWeight: '700', fontSize: 14, marginLeft: 8 },

  footer: { fontSize: 11, color: Colors.textSecondary, textAlign: 'center', marginTop: 20, fontStyle: 'italic' },
});
